import { Coachmark, DirectionalHint, TeachingBubbleContent, IButtonProps, IPositioningContainerProps } from '@fluentui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { BwjEventArgs } from '../../entities/bwj-event';
import { IActionItemProps } from '../bwj-listing/entities';

interface IBwjTutorialPointProps {
  id: string; // identifier unique to SPA
  hostElement: HTMLElement | null; // element for point to attach to
  position: DirectionalHint; // placement of the point relative to the host element
  showTutorialArgs: BwjEventArgs<boolean> | undefined; // can toggle display on or off, if off, onDismissed callback not called
  title: string; // Title of tutorial content
  textContent?: string | undefined; // Text only tutorial content
  content?: JSX.Element | undefined; // Rich tutorial content. Using both text-only and rich content is possible, but ill advised. Choose one or the other
  primaryButton?: IActionItemProps | undefined; // Properties for primary button
  secondaryButton?: IActionItemProps | undefined; // Properties for secondary button
  onDismissed?: ((id: string) => void) | undefined; // Callback invoked when tutorial point dismissed by user manipulation
  closeButtonLabel?: string | undefined; // Label for close button at upper right used by assistive tech.  Default 'Close'
  ariaLabel?: string | undefined; // Description of what this component is, for the benefit of assistive tech.  Default 'Interactive tutorial point'
  ariaDisplayedAlert?: string | undefined; // Alert to assistive tech when point appears.  Default 'A tutorial point just appeared'
  ariaViewingInstructions?: string | undefined; // Note to assistive tech on how to read point content. Default 'Use Alt or Option + C to open the tutorial point'
}

const toButtonProps = (props: IActionItemProps | undefined) => {
  if(!props) { return undefined; }

  const btnProps: IButtonProps = {
    iconProps: !!props.icon ? { iconName: props.icon } : undefined,
    text: props.text,
    onClick: props.onClick,
  };
  return btnProps;
}

export const BwjTutorialPoint: React.FunctionComponent<IBwjTutorialPointProps> = (props) => {
  const { showTutorialArgs, position, primaryButton: pb, secondaryButton: sb } = props;
  const [showPoint, setShowPoint] = useState(false);
  const positionProps = useMemo<IPositioningContainerProps>(() => { return { directionalHint: position, doNotLayer: false }}, [position]);
  const primaryButton = useMemo(() => { return toButtonProps(pb); }, [pb]);
  const secondaryButton = useMemo(() => { return toButtonProps(sb); }, [sb]);

  const onDismiss = () => {
    setShowPoint(false);
    !!props.onDismissed && props.onDismissed(props.id);
  }

  useEffect(() => {
    if(!!showTutorialArgs && showTutorialArgs.accessible()) {
      setShowPoint(showTutorialArgs.getArgs());
    }
  }, [showTutorialArgs]);

  return (
    <>
    {showPoint &&
    <Coachmark
      target={props.hostElement}
      positioningContainerProps={positionProps}
      ariaAlertText={props.ariaDisplayedAlert ?? 'A tutorial point just appeared'}
      ariaDescribedBy={`tutorial-desc-${props.id}`}
      ariaLabelledBy={`tutorial-lbl-${props.id}`}
      ariaDescribedByText={props.ariaViewingInstructions ?? 'Use Alt or Option + C to open the tutorial point'}
      ariaLabelledByText={props.ariaLabel ?? 'Interactive tutorial point'}
    >
      <TeachingBubbleContent
        headline={props.title}
        hasCloseButton={true}
        closeButtonAriaLabel={props.closeButtonLabel ?? 'Close'}
        primaryButtonProps={primaryButton}
        secondaryButtonProps={secondaryButton}
        onDismiss={onDismiss}
        ariaDescribedBy={`tutorial-content-desc-${props.id}`}
        ariaLabelledBy={`tutorial-content-lbl-${props.id}`}
      >
        {!!props.textContent && props.textContent}
        {!!props.content && props.content}
      </TeachingBubbleContent>
    </Coachmark>}
    </>
  );
};
